/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { FeatureBuildingData } from "@/types/content";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { autobind } from "core-decorators";
import { FeatureTypeEnum } from "@/types/enum";
import { MapboxCustomObject } from "./MapboxCustomObject";
import * as mapboxgl from "mapbox-gl";
import { BuildingType } from "@/types/building";
import { RoofType } from "@/types/building";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
// import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  Camera,
  Object3D,
  Scene,
  WebGLRenderer,
  Color,
  Mesh,
  Matrix4,
  Vector3,
  DirectionalLight,
  MeshStandardMaterial,
  MeshLambertMaterial,
  BoxGeometry,
  Group,
  AmbientLight,
  BufferGeometry,
  CylinderGeometry,
} from "three";
import { degToRad } from "three/src/math/MathUtils.js";
// import EditorBuildingController from "@/components/mapbox/Lib/Editors/EditorBuildingController";
import {
  createGableRoof,
  createFlatRoof,
  createDormerRoof,
  createHipRoof,
  createMansardRoof,
  createShedRoof,
  createRoundRoof,
} from "@/utils/building-utils";
import { MAPBOX_MIN_ZOOM } from "@/constant";
import useProjectStore from "@/stores/useProjectStore";
import { rhumbBearing, rhumbDestination } from "@turf/turf";
import { getSunHours } from "../Utils/ObjectUtils";
import { calculateAnnualSunHoursForFeature } from "../Shading/shadeProfile";
import type { GeoJSONFeature } from "mapbox-gl";
export interface BuildingModelConfig {
  scale?: number;
  rotation?: number;
  elevation?: number;
  width: number;
  length: number;
  height: number;
  roofType: RoofType;
  roofAngleDeg: number;
  color: string;
}

// const directionalLight2 = new DirectionalLight(0xffffff);
// directionalLight2.position.set(0, 70, 100).normalize();
// this._threeScene.add(directionalLight2);

export class MapboxBuildingObject extends MapboxCustomObject {
  private _modelConfig: BuildingModelConfig = {
    scale: 1,
    rotation: 0,
    elevation: 0,
    width: 10,
    length: 10,
    height: 10,
    roofType: RoofType.FLAT,
    roofAngleDeg: 20,
    color: "#8b5cf6",
  };
  private _isLoaded: boolean = false;
  private _isHighlighted: boolean = false;
  private _isSelected: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _customLayer: any = null;

  private _threeScene: Scene = new Scene();
  private _threeModel: Object3D | null = null;
  private _camera: Camera = new Camera();
  private _renderer: WebGLRenderer | null = null;
  private _modelTransform: {
    translateX: number;
    translateY: number;
    translateZ: number;
    rotateX: number;
    rotateY: number;
    rotateZ: number;
    scale: number;
  } = {
    translateX: 0,
    translateY: 0,
    translateZ: 0,
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
    scale: 1,
  };
  private _buildingModel: Object3D | null = null;
  private _roofColor: Color = new Color().multiplyScalar(1);
  private _rotatedCorners: number[][] = [];
  private _shadingOverlaySuppressed = false;

  constructor(mapboxViewer: MapboxViewer, content: FeatureBuildingData) {
    super(mapboxViewer, content);

    // Set the editor reference
    this.editor = this._mapboxViewer.editors.editorBuildingController;

    this.init(content);
  }

  set visible(visible: boolean) {
    this._content.config.visible = visible;
    // Visibility is now handled by the shared source in BuildingEntity
    // Update the source to reflect the visibility change
    this._mapboxViewer.entities.buildingEntity?.updateSource();
  }

  get visible() {
    return this._content.config.visible;
  }

  @autobind
  setShadingOverlaySuppressed(suppressed: boolean) {
    this._shadingOverlaySuppressed = suppressed;
    this._mapboxViewer._mapViewer.triggerRepaint();
  }

  @autobind
  async init(content: FeatureBuildingData) {
    this._modelConfig = {
      scale: 1,
      rotation: degToRad(content.properties?.rotation || 0),
      elevation: 0,
      width: content.properties?.width || 10,
      length: content.properties?.length || 10,
      height: content.properties?.height || 10,
      roofType: content.properties?.roofType || RoofType.FLAT,
      roofAngleDeg: content.properties?.roofAngleDeg || 20,
      color: content.config.color || "#8b5cf6",
    };

    this._content = content;
    this._contentId = `${content.id}`;
    // Add to building entity
    this._mapboxViewer.entities.buildingEntity?.add(this);
  }

  @autobind
  getFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length === 0) {
      return null;
    }
    const content = this._content as FeatureBuildingData;

    // For buildings, we use the first coordinate as the center point
    const centerCoordinate = this._content.coordinates[0];

    return {
      type: "Feature",
      id: this._contentId,
      geometry: {
        type: "Point",
        coordinates: centerCoordinate,
      },
      properties: {
        contentId: this._contentId,
        app: "logistic-app",
        name: this._content.name || "Building",
        category: FeatureTypeEnum.BUILDING,
        scale: this._modelConfig.scale,
        rotation: this._modelConfig.rotation,
        elevation: this._modelConfig.elevation,
        color: content.config.color || "#8b5cf6",
        isHighlighted: this._isHighlighted,
        isSelected: this._isSelected,
        visible: this.visible,
        buildingType:
          content.properties?.buildingType || BuildingType.RESIDENTIAL,
        roofType: content.properties?.roofType || RoofType.FLAT,
        height: this._modelConfig.height || 5,
        base_height: 0,
      },
    };
  }

  @autobind
  getPolygonFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length === 0) {
      return null;
    }

    const centerCoordinate = this._content.coordinates[0];
    const width = this._content.properties?.width || 10;
    const length = this._content.properties?.length || 10;
    const rotationDeg = this._content.properties?.rotation || 0;
    const rotation = degToRad(rotationDeg);

    // Create a rectangle polygon based on width and length
    const halfWidth = width / 2;
    const halfLength = length / 2;

    // Create rectangle corners (counter-clockwise)
    const corners = [
      [-halfWidth, -halfLength],
      [halfWidth, -halfLength],
      [halfWidth, halfLength],
      [-halfWidth, halfLength],
      [-halfWidth, -halfLength], // Close the polygon
    ];

    // Rotate and translate the corners
    const rotatedCorners = corners.map(([x, y]) => {
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const rotatedX = x * cos - y * sin;
      const rotatedY = x * sin + y * cos;
      const latRad = (centerCoordinate[1] * Math.PI) / 180;
      const metersPerDegLat = 111000; // approx
      const metersPerDegLng = 111000 * Math.cos(latRad); // adjust for latitude
      return [
        centerCoordinate[0] + rotatedX / metersPerDegLng,
        centerCoordinate[1] + rotatedY / metersPerDegLat,
      ];
    });

    this._rotatedCorners = rotatedCorners;

    return {
      type: "Feature",
      id: this._contentId,
      geometry: {
        type: "Polygon",
        coordinates: [rotatedCorners],
      },
      properties: {
        contentId: this._contentId,
        app: "logistic-app",
        name: this._content.name || "Building",
        category: FeatureTypeEnum.BUILDING,
        color: this._content.config.color || "#8b5cf6",
        isHighlighted: this._isHighlighted,
        isSelected: this._isSelected,
        visible: false,
        buildingType:
          this._content.properties?.buildingType || BuildingType.RESIDENTIAL,
        roofType: this._content.properties?.roofType || RoofType.FLAT,
        roofAngleDeg: this._modelConfig.roofAngleDeg || 20,
        rotation: rotation,
        width: width,
        length: length,
        height: this._modelConfig.height || 5,
        base_height: 0,
      },
    };
  }

  @autobind
  getClearanceAreaFeature(): GeoJSON.Feature | null {
    const footprint = this.getPolygonFeature();
    if (!footprint) return null;
    const coords = (footprint.geometry as GeoJSON.Polygon).coordinates[0] as [
      number,
      number,
    ][];
    if (!coords || coords.length < 4) return null;
    const projectClearanceDistance =
      useProjectStore.getState().project?.clearanceDistance || 3;
    const clearanceDistanceMeters = Math.max(
      (this._content as FeatureBuildingData).properties?.clearanceDistance || 0, // fallback 0
      projectClearanceDistance,
    );

    const show = this._content.properties?.showClearanceArea || false;
    if (!show || clearanceDistanceMeters <= 0) {
      return null;
    }

    const color = "#ff0000";

    if (this._content.properties?.roofType === RoofType.ROUND) {
      const center = (this._content.coordinates[0] || [0, 0]) as [
        number,
        number,
      ];
      const baseRadius = (this._content as FeatureBuildingData).properties
        ?.width
        ? (this._content as FeatureBuildingData).properties!.width / 2
        : 0;
      const outerRadius = baseRadius + clearanceDistanceMeters;
      if (outerRadius <= 0) return null;

      const outerRing = this.generateCircleCoordinates(center, outerRadius, 96);
      const innerRing = this.generateCircleCoordinates(
        center,
        baseRadius,
        96,
      ).reverse();

      return {
        type: "Feature",
        id: `${this._contentId}-clearance`,
        geometry: { type: "Polygon", coordinates: [outerRing, innerRing] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          visible: this.visible,
          color,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      } as GeoJSON.Feature;
    }

    // Build per-edge bands like polygon clearance
    const isCW = this.isClockwise(coords as unknown as number[][]);
    const perpTurn = isCW ? 90 : -90;
    const bands: number[][][][] = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i];
      const b = coords[i + 1];
      if (Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12) {
        continue;
      }
      const segBearing = rhumbBearing(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: a },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: b },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
      );
      const outwardBearing = segBearing - perpTurn;
      const aOffPoint = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: a },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        clearanceDistanceMeters,
        outwardBearing,
        { units: "meters" },
      );
      const bOffPoint = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: b },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        clearanceDistanceMeters,
        outwardBearing,
        { units: "meters" },
      );
      const aOff = (aOffPoint.geometry as GeoJSON.Point)
        .coordinates as number[];
      const bOff = (bOffPoint.geometry as GeoJSON.Point)
        .coordinates as number[];
      const rectRing: number[][] = [
        [a[0], a[1]],
        [b[0], b[1]],
        [bOff[0], bOff[1]],
        [aOff[0], aOff[1]],
        [a[0], a[1]],
      ];
      bands.push([rectRing]);
    }

    if (!bands.length) return null;

    return {
      type: "Feature",
      id: `${this._contentId}-clearance`,
      geometry: { type: "MultiPolygon", coordinates: bands },
      properties: {
        app: "logistic-app",
        contentId: this._contentId,
        visible: this.visible,
        color,
        clearanceDistance: clearanceDistanceMeters,
        showClearanceArea: true,
      },
    } as GeoJSON.Feature;
  }

  @autobind
  getClearanceLinesFeatures(): GeoJSON.Feature[] {
    const footprint = this.getPolygonFeature();
    if (!footprint) return [];
    const coords = (footprint.geometry as GeoJSON.Polygon).coordinates[0] as [
      number,
      number,
    ][];
    if (!coords || coords.length < 4) return [];

    const projectClearanceDistance =
      useProjectStore.getState().project?.clearanceDistance || 3;
    const clearanceDistanceMeters = Math.max(
      (this._content as FeatureBuildingData).properties?.clearanceDistance || 0,
      projectClearanceDistance,
    );

    const show = this._content.properties?.showClearanceArea || false;
    if (!show || clearanceDistanceMeters <= 0) {
      return [];
    }
    const color = "#ff0000";
    // Round roof: draw the outer circle as a line
    if (this._content.properties?.roofType === RoofType.ROUND) {
      const center = (this._content.coordinates[0] || [0, 0]) as [
        number,
        number,
      ];
      const baseRadius = (this._content as FeatureBuildingData).properties
        ?.width
        ? (this._content as FeatureBuildingData).properties!.width / 2
        : 0;
      const outerRadius = baseRadius + clearanceDistanceMeters;
      if (outerRadius <= 0) return [];
      const outerRing = this.generateCircleCoordinates(
        center,
        outerRadius,
        128,
      );
      return [
        {
          type: "Feature",
          geometry: { type: "LineString", coordinates: outerRing },
          properties: {
            app: "logistic-app",
            contentId: this._contentId,
            color,
            visible: this.visible,
            clearanceDistance: clearanceDistanceMeters,
            showClearanceArea: true,
          },
        } as GeoJSON.Feature,
      ];
    }

    const isCW = this.isClockwise(coords as unknown as number[][]);
    const perpTurn = isCW ? 90 : -90;

    const features: GeoJSON.Feature[] = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i];
      const b = coords[i + 1];
      if (Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12) {
        continue;
      }
      const segBearing = rhumbBearing(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: a },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: b },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
      );
      const outwardBearing = segBearing - perpTurn;
      const aOffPoint = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: a },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        clearanceDistanceMeters,
        outwardBearing,
        { units: "meters" },
      );
      const bOffPoint = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: b },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        clearanceDistanceMeters,
        outwardBearing,
        { units: "meters" },
      );
      const aOff = (aOffPoint.geometry as GeoJSON.Point)
        .coordinates as number[];
      const bOff = (bOffPoint.geometry as GeoJSON.Point)
        .coordinates as number[];

      // Opposite edge (parallel)
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [aOff, bOff] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color,
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      });
      // Perpendicular connectors
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [a, aOff] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          color,
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      });
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [b, bOff] },
        properties: {
          app: "logistic-app",
          contentId: this._contentId,
          category: FeatureTypeEnum.BUILDING,
          color,
          visible: this.visible,
          clearanceDistance: clearanceDistanceMeters,
          showClearanceArea: true,
        },
      });
    }

    return features;
  }

  // Reuse polygon winding check for building footprint
  private isClockwise(coordinates: number[][]): boolean {
    let area = 0;
    const n = coordinates.length;
    for (let i = 0; i < n - 1; i++) {
      const j = (i + 1) % n;
      area +=
        (coordinates[j][0] - coordinates[i][0]) *
        (coordinates[j][1] + coordinates[i][1]);
    }
    return area > 0;
  }

  private generateCircleCoordinates(
    center: [number, number],
    radiusMeters: number,
    segments: number = 64,
  ): number[][] {
    const ring: number[][] = [];
    for (let i = 0; i < segments; i++) {
      const bearing = (360 * i) / segments;
      const pt = rhumbDestination(
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: center },
          properties: {},
        } as GeoJSON.Feature<GeoJSON.Point>,
        radiusMeters,
        bearing,
        { units: "meters" },
      );
      ring.push((pt.geometry as GeoJSON.Point).coordinates as number[]);
    }
    if (ring.length) ring.push([...ring[0]]);
    return ring;
  }

  @autobind
  getRotationHandleFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length === 0) {
      return null;
    }
    const center = this._content.coordinates[0];
    const width = this._content.properties?.width || 10;
    const length = this._content.properties?.length || 10;
    const rotationDeg = this._content.properties?.rotation || 0;
    const rotation = degToRad(rotationDeg);

    // Place handle outside footprint along forward direction.
    // Offset rule: ROUND -> width + extra; others -> max(width, length) + extra
    const extraMeters = 2;
    const distanceMeters =
      (this._content.properties?.roofType === RoofType.ROUND
        ? width
        : Math.max(width, length)) + extraMeters;
    // Align handle with building's facing direction (not opposite)
    const offsetX = -Math.sin(rotation) * distanceMeters; // x to east when heading north
    const offsetY = -Math.cos(rotation) * distanceMeters; // y to north

    const latRad = (center[1] * Math.PI) / 180;
    const metersPerDegLat = 111000;
    const metersPerDegLng = 111000 * Math.cos(latRad);
    const handleLng = center[0] + offsetX / metersPerDegLng;
    const handleLat = center[1] + offsetY / metersPerDegLat;

    return {
      type: "Feature",
      id: `${this._contentId}-rotation`,
      geometry: {
        type: "Point",
        coordinates: [handleLng, handleLat],
      },
      properties: {
        app: "logistic-app",
        contentId: this._contentId,
        category: FeatureTypeEnum.BUILDING,
        subtype: "rotation_handle",
        visible: this.visible,
        isSelected: this._isSelected,
        color: this._content.config.color || "#8b5cf6",
      },
    };
  }

  @autobind
  calculateModelTransform() {
    const centerCoordinate = this._content.coordinates[0];
    const modelOrigin = {
      lng: centerCoordinate[0],
      lat: centerCoordinate[1],
    };
    const modelAltitude = this._modelConfig.elevation || 0;
    const modelRotate = [Math.PI / 2, this._modelConfig.rotation || 0, 0];

    const modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
      modelOrigin,
      modelAltitude,
    );

    // Transformation parameters to position, rotate and scale the 3D model
    this._modelTransform = {
      translateX: modelAsMercatorCoordinate.x,
      translateY: modelAsMercatorCoordinate.y,
      translateZ: modelAsMercatorCoordinate.z,
      rotateX: modelRotate[0],
      rotateY: modelRotate[1],
      rotateZ: modelRotate[2],
      scale:
        modelAsMercatorCoordinate.meterInMercatorCoordinateUnits() *
        (this._modelConfig.scale || 1),
    };
  }

  @autobind
  async loadModel(): Promise<void> {
    if (this._isLoaded) {
      return;
    }

    try {
      this.calculateModelTransform();

      // Polygon layers are now managed by BuildingEntity
      // Just create the custom layer for 3D model

      this._customLayer = {
        id: this._contentId,
        type: "custom",
        renderingMode: "3d",
        minzoom: MAPBOX_MIN_ZOOM,
        onAdd: (map: mapboxgl.Map, gl: WebGLRenderingContext) => {
          // Create lights to illuminate the model
          const directionalLight = new DirectionalLight(0xffffff);
          directionalLight.position.set(0, -70, 100).normalize();
          const ambientLight = new AmbientLight(0xffffff, 1);
          this._threeScene.add(directionalLight);
          this._threeScene.add(ambientLight);

          this.reBuildModel();

          this._renderer = new WebGLRenderer({
            canvas: map.getCanvas(),
            context: gl,
            antialias: true,
          });

          this._renderer!.autoClear = false;

          this._isLoaded = true;

          // // Use the three.js GLTF loader to add the 3D model
          // const loader = new GLTFLoader();
          // loader.load(
          //   this._modelConfig.modelUrl,

          //   // eslint-disable-next-line @typescript-eslint/no-explicit-any
          //   (gltf: any) => {
          //     this._isLoaded = true;
          //     this._threeModel = gltf.scene;
          //     console.log("threeModel", this._threeModel);
          //     this._threeScene?.add(this._threeModel!);
          //   },
          //   undefined,
          //   (error: Error) => {
          //     console.error("Error loading GLB model:", error);
          //   }
          // );

          // Use the Mapbox GL JS map canvas for three.js
        },
        render: (_gl: WebGLRenderingContext, matrix: Array<number>) => {
          const rotationX = new Matrix4().makeRotationAxis(
            new Vector3(1, 0, 0),
            this._modelTransform.rotateX,
          );
          const rotationY = new Matrix4().makeRotationAxis(
            new Vector3(0, 1, 0),
            this._modelTransform.rotateY,
          );
          const rotationZ = new Matrix4().makeRotationAxis(
            new Vector3(0, 0, 1),
            this._modelTransform.rotateZ,
          );

          const m = new Matrix4().fromArray(matrix);
          const l = new Matrix4()
            .makeTranslation(
              this._modelTransform.translateX,
              this._modelTransform.translateY,
              this._modelTransform.translateZ,
            )
            .scale(
              new Vector3(
                this._modelTransform.scale,
                -this._modelTransform.scale,
                this._modelTransform.scale,
              ),
            )
            .multiply(rotationX)
            .multiply(rotationY)
            .multiply(rotationZ);
          this._buildingModel!.visible =
            this.visible && !this._shadingOverlaySuppressed;
          this._camera!.projectionMatrix = m.multiply(l);
          this._renderer!.resetState();
          this._renderer!.render(this._threeScene, this._camera!);
          this._mapboxViewer._mapViewer.triggerRepaint();
        },
      };

      // Add the custom layer to the map
      this._mapboxViewer._mapViewer.addLayer(this._customLayer!);

      this._isLoaded = true;
    } catch (error) {
      console.error("Failed to load building model:", error);
    }
  }

  reBuildModel() {
    this._roofColor.set(this._content.config.color!);
    const exturdeColor = new Color("white");
    const dimensions = {
      width: this._modelConfig.width || 10,
      length: this._modelConfig.length || 10,
      height: this._modelConfig.height || 5,
      roofAngleDeg: this._modelConfig.roofAngleDeg || 20,
    };
    const roofType = this._content.properties?.roofType || RoofType.FLAT;
    if (this._buildingModel) {
      const list: Object3D[] = [];
      this._buildingModel.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
          list.push(child);
        }
      });
      this._threeScene.remove(this._buildingModel);
      this._buildingModel = null;
      list.forEach((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
        }
      });
    }
    const group = new Group();
    group.name = "building";

    let geometry: BufferGeometry | null = null;
    if (roofType === RoofType.ROUND) {
      geometry = new CylinderGeometry(
        dimensions.width / 2,
        dimensions.width / 2,
        dimensions.height,
        32,
      );
    } else {
      geometry = new BoxGeometry(
        dimensions.width,
        dimensions.height,
        dimensions.length,
      );
    }

    const material = new MeshLambertMaterial({ color: "white" });
    const building = new Mesh(geometry, material);
    building.position.y = dimensions.height / 2;
    building.castShadow = true;
    // building.receiveShadow = true;
    group.add(building);

    switch (roofType) {
      case RoofType.FLAT: {
        // Flachdach (Flat Roof)
        const flatRoof = createFlatRoof(dimensions, this._roofColor);
        group.add(flatRoof);
        break;
      }

      case RoofType.GABLE: {
        // Satteldach (Gable Roof)
        const gableRoof = createGableRoof(
          dimensions,
          this._roofColor,
          exturdeColor,
        );
        group.add(gableRoof);
        break;
      }

      case RoofType.HIP: {
        // Walmdach (Hip Roof)
        const hipRoof = createHipRoof(dimensions, this._roofColor);
        group.add(hipRoof);
        break;
      }

      case RoofType.SHED: {
        // Shed Roof (Shed Roof)
        const shedRoof = createShedRoof(
          dimensions,
          this._roofColor,
          exturdeColor,
        );
        group.add(shedRoof);
        break;
      }

      case RoofType.DORMER: {
        // Schleppdach (Extended/Dormer Roof)
        const dormerRoof = createDormerRoof(dimensions, this._roofColor);
        group.add(dormerRoof);
        break;
      }

      case RoofType.MANSARD: {
        // Mansarddach (Mansard Roof)
        const mansardRoof = createMansardRoof(
          dimensions,
          this._roofColor,
          exturdeColor,
        );

        group.add(mansardRoof);
        break;
      }

      case RoofType.ROUND: {
        const roundRoof = createRoundRoof(dimensions, this._roofColor);
        group.add(roundRoof);
        break;
      }

      default:
        break;
    }
    this._buildingModel = group;
    this._threeScene.add(group);
  }

  @autobind
  updateContent(content: FeatureBuildingData) {
    this._content = content as FeatureBuildingData;

    const newModelConfig = {
      scale: 1,
      rotation: degToRad(this._content.properties?.rotation || 0),
      elevation: this._content.properties?.elevation || 0,
      width: this._content.properties?.width || 10,
      length: this._content.properties?.length || 10,
      height: this._content.properties?.height || 10,
      roofAngleDeg: this._content.properties?.roofAngleDeg || 20,
      roofType: this._content.properties?.roofType || RoofType.FLAT,
      color: this._content.config.color || "#8b5cf6",
    };
    const propsCheck = {
      width: this._modelConfig.width !== newModelConfig.width,
      length: this._modelConfig.length !== newModelConfig.length,
      height: this._modelConfig.height !== newModelConfig.height,
      roofType: this._modelConfig.roofType !== newModelConfig.roofType,
      roofAngleDeg:
        this._modelConfig.roofAngleDeg !== newModelConfig.roofAngleDeg,
      color: this._modelConfig.color !== newModelConfig.color,
    };
    this._modelConfig = newModelConfig;
    if (
      propsCheck.width ||
      propsCheck.length ||
      propsCheck.height ||
      propsCheck.roofType ||
      propsCheck.color ||
      propsCheck.roofAngleDeg
    ) {
      this.reBuildModel();
    }

    this.calculateModelTransform();

    // Update polygon layer
    this.updatePolygonLayer();

    this._mapboxViewer.entities.buildingEntity?.updateSource();
  }

  @autobind
  updatePolygonLayer() {
    // Polygon data is now managed by BuildingEntity's shared source
    // Just update the shared source
    this._mapboxViewer.entities.buildingEntity?.updateSource();
  }

  @autobind
  setHighlight() {
    this._isHighlighted = true;
    // Update polygon feature state using the shared building source
    const sharedSourceId = "building-source";
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: sharedSourceId, id: this._contentId },
      { highlight: true },
    );
    // Update 3D model highlight
    if (this._threeModel) {
      this._threeModel.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
          (child.material as MeshStandardMaterial).emissive = new Color(
            0xffff00,
          );
        }
      });
    }
  }

  @autobind
  setUnhighlight() {
    this._isHighlighted = false;
    // Update polygon feature state using the shared building source
    const sharedSourceId = "building-source";
    this._mapboxViewer._mapViewer.removeFeatureState(
      { source: sharedSourceId, id: this._contentId },
      "highlight",
    );
    // Update 3D model highlight
    if (this._threeModel) {
      this._threeModel.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
          (child.material as MeshStandardMaterial).emissive = new Color(
            0x000000,
          );
        }
      });
    }
  }

  @autobind
  setSelected() {
    this._isSelected = true;
    // Update polygon feature state using the shared building source
    const sharedSourceId = "building-source";
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: sharedSourceId, id: this._contentId },
      { selected: true },
    );
    // Update source to show rotation handle
    this._mapboxViewer.entities.buildingEntity?.updateSource();
    // Update 3D model selection
    if (this._threeModel) {
      this._threeModel.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
          (child.material as MeshStandardMaterial).emissive = new Color(
            0xff0000,
          );
        }
      });
    }
  }

  @autobind
  setUnselected() {
    this._isSelected = false;
    // Update polygon feature state using the shared building source
    const sharedSourceId = "building-source";
    this._mapboxViewer._mapViewer.removeFeatureState(
      { source: sharedSourceId, id: this._contentId },
      "selected",
    );
    // Update source to hide rotation handle
    this._mapboxViewer.entities.buildingEntity?.updateSource();
    // Update 3D model selection
    if (this._threeModel) {
      this._threeModel.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
          (child.material as MeshStandardMaterial).emissive = new Color(
            0x000000,
          );
        }
      });
    }
  }

  @autobind
  dispose() {
    if (this._isLoaded) {
      // Remove the custom layer
      if (this._mapboxViewer._mapViewer.getLayer(this._contentId)) {
        this._mapboxViewer._mapViewer.removeLayer(this._contentId);
      }

      // Polygon layers and sources are now managed by BuildingEntity
      // No need to remove them here

      // Clean up Three.js resources
      if (this._threeScene) {
        this._threeScene.clear();
      }
      if (this._threeModel) {
        this._threeModel = null;
      }
      if (this._renderer) {
        this._renderer.dispose();
      }

      this._isLoaded = false;
    }

    this._mapboxViewer.entities.buildingEntity?.remove(this._contentId);
  }

  @autobind
  focusOn() {
    const coordinates = this._content.coordinates;
    if (!coordinates || coordinates.length === 0) return;

    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord as mapboxgl.LngLatLike);
    }, new mapboxgl.LngLatBounds());

    this._mapboxViewer._mapViewer.fitBounds(bounds, {
      padding: 50,
      maxZoom: 20,
    });
  }

  @autobind
  async calculateSunHours(stepLng: number = 0.00002, stepLat?: number) {
    const shadeMap = this._mapboxViewer?.shadeMap;
    if (!shadeMap) return 0;

    const targetFeature = this.getPolygonFeature() as GeoJSONFeature | null;

    return getSunHours(
      shadeMap,
      this._rotatedCorners,
      stepLng,
      stepLat,
      targetFeature ?? undefined,
    );
  }

  @autobind
  async getSunHoursAtPoint(): Promise<number> {
    const coords = this._content?.coordinates;
    const center = coords[0] as [number, number];
    const shadeMap = this._mapboxViewer?.shadeMap;
    const targetFeature = this.getPolygonFeature() as GeoJSONFeature | null;
    if (!shadeMap || !targetFeature) return 0;

    const features = await shadeMap.getShadingFeatures();
    return calculateAnnualSunHoursForFeature(
      targetFeature,
      features,
      { lat: center[1], lng: center[0] },
    );
  }
}
